import React, { useState, useMemo, useEffect } from 'react';
import useApiContext from 'hooks/useApiContext';
import useModalContext from 'hooks/useModalContext';
import { useLocation, Outlet, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CopyAdminModal from './CopyAdminModal';
// eslint-disable-next-line import/no-duplicates
import { format } from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import { fr } from 'date-fns/locale';
import useRepo from 'hooks/useRepo';
import { T, A } from 'TOMA';
import s from './UsersList.module.scss';
import routes from 'router/routes';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { CustomLink } from 'components/CustomLink';
import { eRoles } from '../../interfaces/Roles';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import PeopleIcon from '@mui/icons-material/People';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';
import CategoryIcon from '@mui/icons-material/Category';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AddIcon from '@mui/icons-material/Add';

const columnHelper = createColumnHelper<any>();

const SIDEBAR_WIDTH = 230;
// Height of the app's sticky top header (#app-header Toolbar minHeight), so the
// fixed sidebar starts below it instead of being hidden underneath.
const HEADER_HEIGHT = 70;

interface SidebarItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  matchFn: (p: string) => boolean;
}

function Users(): JSX.Element {
  const { setModalContent, setModalVisible } = useModalContext();
  const { pathname } = useLocation();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const { userRepository, codificationRepository, preferenceRepository } =
    useApiContext();
  const user = userRepository.getUser();
  const isSuperAdmin = user?.role === eRoles.SUPER_ADMIN;

  const [filterName, setFilterName] = useState<any>();
  const [reload, setReload] = useState<number>(0);
  const [options, setOptions] = useState<any>();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    response = [],
    loading,
    error,
  } = useRepo(async () => {
    return await userRepository.getUsers();
  }, [reload]);

  const dataOptions = useRepo(async () => {
    return await userRepository.getOptions().then((e) => e);
  }, [pathname]);

  useEffect(() => {
    if (!dataOptions.loading && dataOptions.response) {
      setOptions(dataOptions.response);
    }
  }, [dataOptions.loading, dataOptions.response]);

  const { response: codifCollectif } = useRepo(
    async () => await codificationRepository.get('collectif')
  );

  // Sidebar nav items mirroring the header nav for the current role
  const sidebarItems: SidebarItem[] = useMemo(() => {
    if (user?.role === eRoles.SUPER_ADMIN) {
      return [
        {
          label: t('header:users'),
          icon: <PeopleIcon fontSize="small" />,
          path: routes.users.path,
          matchFn: (p) => p.includes('users'),
        },
        {
          label: t('header:universes'),
          icon: <PublicIcon fontSize="small" />,
          path: routes.universes.path,
          matchFn: (p) => p.includes('universes'),
        },
        {
          label: t('header:categories'),
          icon: <CategoryIcon fontSize="small" />,
          path: routes.categories.path,
          matchFn: (p) =>
            p.includes('categories') && !p.includes('subcategories'),
        },
        {
          label: t('header:subcategories'),
          icon: <AccountTreeIcon fontSize="small" />,
          path: routes.subcategories.path,
          matchFn: (p) => p.includes('subcategories'),
        },
      ];
    }
    if (user?.role === eRoles.ADMIN) {
      return [
        {
          label: t('header:users'),
          icon: <PeopleIcon fontSize="small" />,
          path: routes.profile.path,
          matchFn: (p) => p.includes('users'),
        },
        {
          label: t('header:events'),
          icon: <CalendarMonthIcon fontSize="small" />,
          path: routes.admEventList.path,
          matchFn: (p) => p.includes('events'),
        },
        {
          label: t('header:exhibitor'),
          icon: <GroupsIcon fontSize="small" />,
          path: routes.home.path.replace(/\/$/, ''),
          matchFn: (p) => p.includes('exhibitor'),
        },
        {
          label: t('header:company_settings'),
          icon: <BusinessIcon fontSize="small" />,
          path: routes.company.path,
          matchFn: (p) => p.includes('company'),
        },
      ];
    }
    return [];
  }, [user?.role, t]);

  const openCopyAdminModal = (adminId: number): void => {
    setModalContent(
      <CopyAdminModal
        adminId={adminId}
        onClose={() => setModalVisible(false)}
        onSuccess={() => {
          setModalVisible(false);
          toast.success(t('users:copy_admin_success'));
          setReload((e) => e + 1);
        }}
      />
    );
    setModalVisible(true);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      ...(user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN'
        ? [
            columnHelper.accessor('remove', {
              header: '',
              enableSorting: false,
              cell: ({ row }) => {
                const { edit, removed, role } = row.original;
                if (role !== 'MODERATOR') return null;
                return (
                  <button
                    type="button"
                    className={s.ExhibitorList__remove}
                    onClick={() => {
                      setModalContent(
                        <div>
                          <A.Text fontSize="22" fw="bold" mb="20">
                            {t('users:confirm_remove_user')}
                          </A.Text>
                          <T.Flex gap={2} valign="center" mb="-15">
                            <A.Button
                              bg="alert"
                              onClick={async () => {
                                await userRepository.remove(edit.id);
                                setModalVisible(false);
                                setReload((e) => e + 1);
                              }}
                            >
                              {t('users:confirm')}
                            </A.Button>
                            <A.Button
                              appearance="link"
                              color="grey-80"
                              mb="15"
                              textDecoration="underline"
                              onClick={() => setModalVisible(false)}
                            >
                              {t('users:cancel')}
                            </A.Button>
                          </T.Flex>
                        </div>
                      );
                      setModalVisible(true);
                    }}
                  >
                    {removed ? (
                      <A.Icon icon="trash3-fill" color="alert" fontSize="22" />
                    ) : (
                      <A.Icon icon="trash2" color="alert" fontSize="22" />
                    )}
                  </button>
                );
              },
            }),
          ]
        : []),
      columnHelper.accessor('role', {
        header: t('users:role'),
      }),
      columnHelper.accessor('count_exhibitors', {
        header: t('users:number_of_authorized_exhibitors'),
        cell: ({ getValue }) => {
          const { count, role } = getValue();
          if (role === 'EXHIBITOR') return '-';
          return count;
        },
      }),
      columnHelper.accessor('firstname', {
        header: t('users:firstname'),
      }),
      columnHelper.accessor('lastname', {
        header: t('users:lastname'),
      }),
      columnHelper.accessor('email', {
        header: t('users:email'),
      }),
      columnHelper.accessor('last_login_at', {
        header: t('users:last_login'),
      }),
      columnHelper.accessor('edit', {
        header: '',
        enableSorting: false,
        cell: ({ getValue }) => {
          const { id } = getValue();
          return (
            <CustomLink to={routes.home2.path + `users/${id}`}>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  fontSize: 12,
                  borderRadius: '6px',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  borderColor: '#ddd',
                  color: '#333',
                }}
              >
                {t('exhibitors:edit')}
              </Button>
            </CustomLink>
          );
        },
      }),
      columnHelper.accessor('copyAdmin', {
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const { role, edit } = row.original;
          if (role !== 'ADMIN') return null;
          return (
            <Button
              variant="outlined"
              size="small"
              sx={{
                fontSize: 12,
                borderRadius: '6px',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                borderColor: '#ddd',
                color: '#333',
              }}
              onClick={() => openCopyAdminModal(edit.id)}
            >
              {t('users:copy')}
            </Button>
          );
        },
      }),
      ...(user?.role === 'SUPER_ADMIN'
        ? [
            columnHelper.accessor('company', {
              header: t('users:company'),
              cell: ({ getValue }) => {
                const { id, role } = getValue();
                if (role !== 'MODERATOR') return '-';
                return (
                  <CustomLink
                    to={routes.home2.path + `users/${id}/new-company`}
                    color="info"
                  >
                    {t('users:add_company')}
                  </CustomLink>
                );
              },
            }),
          ]
        : []),
    ];

    if (user?.role !== 'ADMIN' && user?.role !== 'SUPER_ADMIN') {
      baseColumns.splice(
        1,
        0,
        columnHelper.accessor('count_moderators', {
          header: t('admin:number_of_moderators'),
          cell: ({ getValue }) => {
            const { count, role } = getValue();
            if (role === 'EXHIBITOR' || role === 'MODERATOR') return '-';
            return count;
          },
        })
      );
      baseColumns.splice(
        1,
        0,
        columnHelper.accessor('moderators', {
          header: 'Moderators',
          cell: ({ getValue }) => {
            const { count, role } = getValue();
            if (role === 'EXHIBITOR' || role === 'MODERATOR') return '-';
            return count;
          },
        })
      );
    }

    return baseColumns;
  }, [user]);

  const data = useMemo(
    () =>
      // @ts-expect-error
      response?.map((e: any) => ({
        role: e.role,
        count_exhibitors: { count: e.count_of_exhibitors, role: e.role },
        count_moderators: { count: e.count_of_moderators, role: e.role },
        firstname: e.firstname,
        lastname: e.lastname,
        email: e.email,
        moderators: { count: e.moderators, role: e.role },
        last_login_at: e.last_login_at
          ? format(new Date(e.last_login_at), 'dd/MM/yyyy HH:mm', {
              locale: fr,
            })
          : '',
        edit: { id: e.id, role: e.role },
        company: { id: e.id, role: e.role },
        removed: e.removed,
      })),
    [response, codifCollectif, filterName]
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    manualPagination: true,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  // Drawer is open when we're on a user detail/create path
  const isDrawerOpen =
    pathname !== routes.users.path && pathname.includes('/users/');

  const closeDrawer = (): void => {
    setReload((prev) => prev + 1);
    navigate(routes.users.path);
  };

  const canCreateUser =
    isSuperAdmin ||
    (options && (options.moderators > 0 || options.exhibitors > 0));

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F9FAFB' }}>
      {/* ── Left sidebar ── */}
      {sidebarItems.length > 0 && (
        <Box
          component="nav"
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            bgcolor: '#fff',
            borderRight: '1px solid #eee',
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            top: HEADER_HEIGHT,
            left: 0,
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
            overflowY: 'hidden',
          }}
        >
          {/* Sidebar header */}
          <Box sx={{ px: 2, py: 2, borderBottom: '1px solid #f0f0f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '8px',
                  bgcolor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <ManageAccountsIcon sx={{ fontSize: 18, color: '#555' }} />
              </Box>
              <Typography
                sx={{ fontSize: '13px', fontWeight: 600, color: '#111' }}
              >
                Administration
              </Typography>
            </Box>
          </Box>

          {/* Nav items */}
          <List disablePadding sx={{ py: 1.5, flex: 1 }}>
            {sidebarItems.map((item) => {
              const isActive = item.matchFn(pathname);
              return (
                <ListItem key={item.label} disablePadding>
                  <ListItemButton
                    component={CustomLink as any}
                    to={item.path}
                    color="black"
                    textDecoration="none"
                    sx={{
                      px: 2,
                      py: 0.875,
                      mx: 1,
                      mb: 0.25,
                      borderRadius: '8px',
                      backgroundColor: isActive ? '#f5f5f5' : 'transparent',
                      '&:hover': {
                        backgroundColor: isActive ? '#eeeeee' : '#f9f9f9',
                      },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <ListItemIcon
                      sx={{ minWidth: 30, color: isActive ? '#111' : '#bbb' }}
                    >
                      {isActive ? (
                        <CheckIcon sx={{ fontSize: 17 }} />
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '13px',
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? '#111' : '#666',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      {/* ── Main content ── */}
      <Box
        sx={{
          flex: 1,
          px: { xs: 2, md: 4 },
          py: 3,
          minWidth: 0,
          marginLeft: sidebarItems.length > 0 ? `${SIDEBAR_WIDTH}px` : 0,
        }}
      >
        {/* Page header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: '22px', fontWeight: 700, color: '#111' }}
            >
              {t('users:list_of_users')}
            </Typography>
            {!loading && (
              <Typography sx={{ fontSize: '13px', color: '#888', mt: 0.5 }}>
                {data?.length ?? 0} utilisateur
                {(data?.length ?? 0) !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <TextField
              size="small"
              placeholder="Rechercher un utilisateur..."
              onChange={(e) => setFilterName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: '8px', fontSize: 13, bgcolor: '#fff' },
              }}
              sx={{ width: 240 }}
            />
            {canCreateUser && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                component={CustomLink as any}
                to={routes.home2.path + 'users/new-user'}
                textDecoration="none"
                sx={{
                  height: 40,
                  fontSize: 13,
                  bgcolor: '#111',
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#333' },
                }}
              >
                {t('users:create_a_new_moderator')}
              </Button>
            )}
          </Box>
        </Box>

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} sx={{ color: '#000' }} />
          </Box>
        )}

        {/* Table */}
        {!loading && (
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: '10px',
              border: '1px solid #eee',
              overflowX: 'auto',
            }}
          >
            <table className={s.Table} style={{ minWidth: 700 }}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {header.column.getIsSorted() === 'asc'
                          ? ' ↑'
                          : header.column.getIsSorted() === 'desc'
                          ? ' ↓'
                          : null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => {
                  if (row.original.removed) return null;
                  return (
                    <tr key={row.id} className={s.ExhibitorList__row}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          <div className={s.ExhibitorList__row__div}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        )}
      </Box>

      {/* ── Right-side Drawer for user edit / create ── */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 560 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: '1px solid #eee',
            position: 'sticky',
            top: 0,
            bgcolor: '#fff',
            zIndex: 1,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
            {pathname.includes('new-user')
              ? t('users:create_a_new_moderator')
              : t('exhibitors:edit')}
          </Typography>
          <IconButton size="small" onClick={closeDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <Outlet />
        </Box>
      </Drawer>
    </Box>
  );
}

export default Users;
